/**
 * rate-card.ts
 * locx-service-progress Slice 1 — Shared Zod contracts for servicios.rate_card
 * and servicios.rate_card_item (versioned tariff for LOCX certifiable items).
 *
 * Spec: SL-44 (rate_card schema) / openspec/changes/2026-07-02-locx-service-progress
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// RateCardStatusSchema — closed vocabulary for rate_card.status (SL-44.4)
// ---------------------------------------------------------------------------

/**
 * Closed vocabulary: 'draft' | 'active' | 'expired'. Any other value is
 * rejected with a ZodError at the application boundary. The DB CHECK
 * constraint in migration 0045 provides a defense-in-depth backstop.
 *
 * Spec: SL-44.4
 */
export const RateCardStatusSchema = z.enum(['draft', 'active', 'expired'], {
  errorMap: () => ({ message: "status must be 'draft', 'active', or 'expired'" }),
});

export type RateCardStatusContract = z.infer<typeof RateCardStatusSchema>;

// ---------------------------------------------------------------------------
// RateCardCreateSchema (SL-44.1, SL-44.5)
// ---------------------------------------------------------------------------

/**
 * Request body for POST /servicios/rate-cards.
 * companyId is NEVER accepted from the body (KC-3.5) — always from TenantContext.
 * clientId: nullable — null/absent means a GENERAL rate card (SL-44 / design §2b).
 *
 * Spec: SL-44.1, SL-44.5
 */
export const RateCardCreateSchema = z
  .object({
    clientId: z
      .string()
      .uuid()
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    currency: z.string().min(1, 'currency must not be empty').max(10, 'currency must be at most 10 characters'),
    validFrom: z.string().datetime({ offset: true }),
    validTo: z
      .string()
      .datetime({ offset: true })
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    status: RateCardStatusSchema.optional().default('draft'),
  })
  .strict();

export type RateCardCreate = z.infer<typeof RateCardCreateSchema>;

// ---------------------------------------------------------------------------
// RateCardItemCreateSchema (SL-44.2, SL-44.5)
// ---------------------------------------------------------------------------

/**
 * Request body for POST /servicios/rate-cards/:id/items.
 * unitPrice: numeric string (e.g. '1500.00') — mirrors AssetPricingSchema's
 * unitPrice convention (SL-37.5) since both map onto Postgres `numeric` columns.
 *
 * Spec: SL-44.2, SL-44.5
 */
export const RateCardItemCreateSchema = z
  .object({
    itemCode: z.string().min(1, 'itemCode must not be empty').max(64, 'itemCode must be at most 64 characters'),
    unit: z.string().min(1, 'unit must not be empty').max(32, 'unit must be at most 32 characters'),
    unitPrice: z
      .string()
      .regex(/^\d+(\.\d+)?$/, 'unitPrice must be a numeric string (e.g. "1500.00")'),
  })
  .strict();

export type RateCardItemCreate = z.infer<typeof RateCardItemCreateSchema>;

// ---------------------------------------------------------------------------
// RateCardUpdateSchema / RateCardItemUpdateSchema — edit-in-place (SL-44 follow-up)
// ---------------------------------------------------------------------------

/**
 * Request body for PATCH /servicios/rate-cards/:id.
 * Only status and validTo are editable — clientId/currency/validFrom stay
 * immutable (they define the card's identity/pricing basis; changing them
 * post-creation would be ambiguous). At least one field is required.
 */
export const RateCardUpdateSchema = z
  .object({
    status: RateCardStatusSchema.optional(),
    validTo: z
      .string()
      .datetime({ offset: true })
      .nullable()
      .optional(),
  })
  .strict()
  .refine((v) => v.status !== undefined || v.validTo !== undefined, {
    message: 'at least one of status or validTo must be provided',
  });

export type RateCardUpdate = z.infer<typeof RateCardUpdateSchema>;

/**
 * Request body for PATCH /servicios/rate-cards/:id/items/:itemId.
 * itemCode stays immutable (it's the identity of the priced line within the
 * card — renaming it is a new item, not an edit). At least one field required.
 */
export const RateCardItemUpdateSchema = z
  .object({
    unit: z.string().min(1, 'unit must not be empty').max(32, 'unit must be at most 32 characters').optional(),
    unitPrice: z
      .string()
      .regex(/^\d+(\.\d+)?$/, 'unitPrice must be a numeric string (e.g. "1500.00")')
      .optional(),
  })
  .strict()
  .refine((v) => v.unit !== undefined || v.unitPrice !== undefined, {
    message: 'at least one of unit or unitPrice must be provided',
  });

export type RateCardItemUpdate = z.infer<typeof RateCardItemUpdateSchema>;

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

/** Spec: SL-44.5 */
export const RateCardResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  clientId: z.string().uuid().nullable(),
  currency: z.string(),
  validFrom: z.string().datetime().or(z.date()),
  validTo: z.string().datetime().or(z.date()).nullable(),
  status: RateCardStatusSchema,
  createdAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
});

export type RateCardResponse = z.infer<typeof RateCardResponseSchema>;

/** Spec: SL-44.5 */
export const RateCardItemResponseSchema = z.object({
  id: z.string().uuid(),
  rateCardId: z.string().uuid(),
  itemCode: z.string(),
  unit: z.string(),
  unitPrice: z.string(),
});

export type RateCardItemResponse = z.infer<typeof RateCardItemResponseSchema>;
